/**
 * useForecastTrends Hook
 *
 * Fetches forecast data across different model runs for a specific date
 * to show how forecasts have evolved over time.
 *
 * Uses available-runs as the source of truth, then fetches each run with
 * `run=` on the fast path (same shape as map/timeline). Avoids one
 * unbounded all-runs /forecasts query that expands to leaves × all runs.
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/api/client";
import { ENDPOINTS } from "@/api/endpoints";
import type {
  ForecastDataItem,
  ForecastVariableData,
  ForecastDataResponse,
} from "@/api/types/forecasts";
import type { StormImpactModel } from "@/types";
import { shouldProgressiveEvolution } from "./evolutionScale";

/** Preserve prior Evolution intent of at most ~50 run points. */
export const MAX_EVOLUTION_RUNS = 50;

/** Cap parallel /forecasts calls so we do not overload the API. */
const EVOLUTION_FETCH_CONCURRENCY = 5;

export interface TrendDataPoint {
  run: string;
  runTimestamp: number;
  date: string;
  nodeId: string;
  [key: string]: string | number;
}

export interface TrendStatistics {
  variable: string;
  variableName: string;
  units: string;
  currentValue: number | null;
  previousValue: number | null;
  change: number | null;
  percentChange: number | null;
  maxValue: number | null;
  minValue: number | null;
}

interface UseForecastTrendsParams {
  nodeId?: string;
  modelId?: string;
  weatherModelId?: string;
  forecastDate?: string;
  stormImpactModel?: StormImpactModel;
  /**
   * Runs from GET /forecasts/available-runs (newest first).
   * When provided (including empty), used as the sole run list — no all-runs
   * /forecasts query. When omitted, the hook fetches available-runs itself.
   */
  availableRuns?: string[];
  /** When true, wait before fetching (available-runs still in flight). */
  availableRunsLoading?: boolean;
  /** When false, skips the fetch. Use to lazy-load (e.g. only when Evolution tab is active). */
  enabled?: boolean;
  /**
   * Leaf count of the selected scope. Large scopes make each run slower, so the
   * chart paints after the first batch instead of waiting for every run.
   */
  scopeLeafCount?: number;
}

export interface EvolutionFetchResult {
  items: ForecastDataItem[];
  requestedRuns: string[];
  failedRuns: string[];
}

function abortError(): Error {
  const err = new Error("Evolution fetch aborted");
  err.name = "AbortError";
  return err;
}

async function fetchForecastsForRuns(params: {
  nodeId: string;
  modelId: string;
  weatherModelId: string;
  forecastDate: string;
  runs: string[];
  signal?: AbortSignal;
  /** Called after each batch with everything fetched so far (newest runs first). */
  onBatchComplete?: (itemsSoFar: ForecastDataItem[]) => void;
}): Promise<EvolutionFetchResult> {
  const {
    nodeId,
    modelId,
    weatherModelId,
    forecastDate,
    runs,
    signal,
    onBatchComplete,
  } = params;
  if (runs.length === 0) {
    return { items: [], requestedRuns: runs, failedRuns: [] };
  }

  const items: ForecastDataItem[] = [];
  const failedRuns: string[] = [];
  let firstError: unknown;

  for (let i = 0; i < runs.length; i += EVOLUTION_FETCH_CONCURRENCY) {
    // Superseded generations (filter change / unmount) must not keep issuing
    // batches and competing with the primary map/grid requests.
    if (signal?.aborted) throw abortError();

    const batch = runs.slice(i, i + EVOLUTION_FETCH_CONCURRENCY);
    // allSettled: one failed run must not blank the whole chart. Complete
    // failure is still surfaced as an error below.
    const batchResults = await Promise.allSettled(
      batch.map(async (run) => {
        const response = await api.get<ForecastDataResponse>(
          ENDPOINTS.FORECASTS,
          {
            signal,
            params: {
              modelId,
              weatherModelId,
              geographicNodeIds: nodeId,
              min_date: forecastDate,
              max_date: forecastDate,
              run,
              // Same as grid: backend multiplies by leaf count for non-leaves.
              limit: 200,
            },
          },
        );
        return response.items || [];
      }),
    );

    batchResults.forEach((result, index) => {
      if (result.status === "fulfilled") {
        items.push(...result.value);
      } else {
        failedRuns.push(batch[index]);
        if (firstError === undefined) firstError = result.reason;
      }
    });

    // Show the newest runs while the remaining batches are still in flight.
    if (onBatchComplete && items.length > 0 && !signal?.aborted) {
      onBatchComplete([...items]);
    }
  }

  if (signal?.aborted) throw abortError();

  // Every run failed → this is an error, not an empty chart.
  if (failedRuns.length === runs.length) {
    throw firstError ?? new Error("Failed to load forecast evolution");
  }

  return { items, requestedRuns: runs, failedRuns };
}

function buildTrendResult(
  forecasts: ForecastDataItem[],
  nodeId: string | undefined,
  stormImpactModel: StormImpactModel | undefined,
) {
  if (forecasts.length === 0) {
    return { data: [], statistics: [], availableVariables: [] };
  }

  const isOrgRoot = nodeId === "org-root";
  const directMatches = forecasts.filter(
    (forecast) => forecast.geographic_node_id === nodeId,
  );
  const itemsToProcess =
    isOrgRoot || directMatches.length === 0 ? forecasts : directMatches;

  // Keep org-root behavior consistent with timeline: choose one stable root per run.
  const sourceForecasts = isOrgRoot
    ? [...itemsToProcess]
        .sort((a, b) => {
          const runCompare = a.run.localeCompare(b.run);
          if (runCompare !== 0) return runCompare;
          return a.geographic_node_id.localeCompare(b.geographic_node_id);
        })
        .filter((forecast, index, arr) => {
          if (index === 0) return true;
          return forecast.run !== arr[index - 1].run;
        })
    : itemsToProcess;

  if (sourceForecasts.length === 0) {
    return { data: [], statistics: [], availableVariables: [] };
  }

  const trendsData: TrendDataPoint[] = sourceForecasts.map(
    (forecast: ForecastDataItem) => {
      const dataPoint: TrendDataPoint = {
        run: forecast.run,
        runTimestamp: new Date(forecast.run).getTime(),
        date: forecast.forecast_date,
        nodeId: forecast.geographic_node_id,
      };

      forecast.variable_values?.forEach((varValue: ForecastVariableData) => {
        const variableKey = varValue.variable_slug;
        const value = parseFloat(varValue.value);
        if (!isNaN(value) && varValue.variable_type === "model") {
          dataPoint[variableKey] = value;
        }
      });

      return dataPoint;
    },
  );

  const validTrendsData = trendsData.filter(
    (item) => Object.keys(item).length > 4,
  );

  validTrendsData.sort((a, b) => b.runTimestamp - a.runTimestamp);

  const variableOrder: string[] = [];
  const variableSet = new Set<string>();
  const excludeKeys = ["run", "runTimestamp", "date", "nodeId"];

  sourceForecasts.forEach((forecast) => {
    forecast.variable_values?.forEach((varValue) => {
      if (varValue.variable_type === "model") {
        const key = varValue.variable_slug;
        if (!variableSet.has(key)) {
          variableSet.add(key);
          variableOrder.push(key);
        }
      }
    });
  });

  validTrendsData.forEach((item) => {
    Object.keys(item).forEach((key) => {
      if (!excludeKeys.includes(key) && typeof item[key] === "number") {
        if (!variableSet.has(key)) {
          variableSet.add(key);
          variableOrder.push(key);
        }
      }
    });
  });

  const variables =
    variableOrder.length > 0 ? variableOrder : Array.from(variableSet);

  const defaultVars = [
    "estimate90th",
    "predicted_median",
    "estimate75th",
    "best_estimate",
  ].filter((v) => variables.includes(v));

  const stats: TrendStatistics[] = defaultVars.map((variableSlug) => {
    const variable = stormImpactModel?.variables?.find(
      (v) => v.variable_slug === variableSlug,
    );
    const variableName =
      variable?.variable_name || formatVariableName(variableSlug);
    const units = variable?.variable_units || "";

    let currentValue: number | null = null;
    let previousValue: number | null = null;
    let maxValue = -Infinity;
    let minValue = Infinity;

    if (validTrendsData.length >= 1) {
      currentValue = validTrendsData[0][variableSlug] as number;
    }
    if (validTrendsData.length >= 2) {
      previousValue = validTrendsData[1][variableSlug] as number;
    }

    validTrendsData.forEach((dataPoint) => {
      const value = dataPoint[variableSlug] as number;
      if (typeof value === "number" && !isNaN(value)) {
        if (value > maxValue) maxValue = value;
        if (value < minValue) minValue = value;
      }
    });

    let change: number | null = null;
    let percentChange: number | null = null;
    if (
      typeof currentValue === "number" &&
      typeof previousValue === "number" &&
      !isNaN(currentValue) &&
      !isNaN(previousValue)
    ) {
      change = currentValue - previousValue;
      percentChange = previousValue !== 0 ? (change / previousValue) * 100 : 0;
    }

    return {
      variable: variableSlug,
      variableName,
      units,
      currentValue:
        typeof currentValue === "number" && !isNaN(currentValue)
          ? currentValue
          : null,
      previousValue:
        typeof previousValue === "number" && !isNaN(previousValue)
          ? previousValue
          : null,
      change: typeof change === "number" && !isNaN(change) ? change : null,
      percentChange:
        typeof percentChange === "number" && !isNaN(percentChange)
          ? percentChange
          : null,
      maxValue: maxValue !== -Infinity ? maxValue : null,
      minValue: minValue !== Infinity ? minValue : null,
    };
  });

  return {
    data: validTrendsData,
    statistics: stats,
    availableVariables: variables,
  };
}

export function useForecastTrends(params: UseForecastTrendsParams) {
  const {
    nodeId,
    modelId,
    weatherModelId,
    forecastDate,
    stormImpactModel,
    availableRuns,
    availableRunsLoading = false,
    enabled: enabledOverride,
    scopeLeafCount = 0,
  } = params;

  const paramsPresent =
    !!nodeId && !!modelId && !!weatherModelId && !!forecastDate;

  // Wait for available-runs when the parent owns that fetch; otherwise the
  // hook will load available-runs itself (drawer / standalone callers).
  const runsReady =
    availableRuns !== undefined
      ? !availableRunsLoading
      : !availableRunsLoading;

  // Empty provided run list → nothing to chart; skip network.
  const hasRunsToFetch =
    availableRuns === undefined || availableRuns.length > 0;

  const isEnabled =
    (enabledOverride ?? true) && paramsPresent && runsReady && hasRunsToFetch;

  const runKey =
    availableRuns !== undefined ? availableRuns.join("|") : "self-fetch-runs";

  const progressive = shouldProgressiveEvolution(scopeLeafCount);

  // Identity of the current selection: partial paints from a previous selection
  // must never be shown against a new one.
  const paintIdentity = [
    nodeId,
    modelId,
    weatherModelId,
    forecastDate,
    stormImpactModel?.model_id,
    runKey,
  ].join("~");

  const [partialPaint, setPartialPaint] = useState<{
    identity: string;
    result: ReturnType<typeof buildTrendResult>;
  } | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: [
      "forecasts",
      "trends",
      {
        nodeId,
        modelId,
        weatherModelId,
        forecastDate,
        stormModelId: stormImpactModel?.model_id,
        runKey,
      },
    ],
    queryFn: async ({ signal }) => {
      let runs = availableRuns;

      if (runs === undefined) {
        const runsResponse = await api.get<{
          runs?: Array<{ run: string }>;
        }>(ENDPOINTS.AVAILABLE_RUNS, {
          signal,
          params: {
            stormImpactModelId: modelId,
            weatherModelId,
            forecast_date: forecastDate,
          },
        });
        runs = (runsResponse.runs || []).map((r) => r.run);
      }

      // available-runs is already newest-first; keep prior Evolution cap.
      const selectedRuns = runs.slice(0, MAX_EVOLUTION_RUNS);

      return fetchForecastsForRuns({
        nodeId: nodeId!,
        modelId: modelId!,
        weatherModelId: weatherModelId!,
        forecastDate: forecastDate!,
        runs: selectedRuns,
        signal,
        onBatchComplete: progressive
          ? (itemsSoFar) =>
              setPartialPaint({
                identity: paintIdentity,
                result: buildTrendResult(itemsSoFar, nodeId, stormImpactModel),
              })
          : undefined,
      });
    },
    enabled: isEnabled,
    // Match other SIP forecast hooks — avoid refetch flicker wiping the chart
    // on window focus for every org.
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    // Global default is retry: 1 — on this fan-out that would replay every run
    // request. Partial failures are already tolerated inside the query fn.
    retry: false,
    select: (result) => ({
      ...buildTrendResult(result.items, nodeId, stormImpactModel),
      failedRuns: result.failedRuns,
      // Newest run missing means the leftmost/"current" statistic is really an
      // older run — callers must be able to say so.
      missingNewestRun:
        result.requestedRuns.length > 0 &&
        result.failedRuns.includes(result.requestedRuns[0]),
    }),
  });

  const waitingForRuns = paramsPresent && availableRunsLoading;
  const failedRuns = data?.failedRuns ?? [];

  // Only usable while the matching query is still running; the settled result
  // always wins.
  const paint =
    data === undefined && partialPaint?.identity === paintIdentity
      ? partialPaint.result
      : undefined;

  return {
    data: data?.data || paint?.data || [],
    statistics: data?.statistics || paint?.statistics || [],
    availableVariables:
      data?.availableVariables || paint?.availableVariables || [],
    // Do not use isFetching — background refetch would remount the chart
    // spinner for all orgs after staleTime.
    loading: waitingForRuns || (isLoading && !paint),
    error,
    partial: failedRuns.length > 0,
    failedRunCount: failedRuns.length,
    missingNewestRun: data?.missingNewestRun ?? false,
  };
}

function formatVariableName(slug: string): string {
  const names: Record<string, string> = {
    estimate25th: "25th Percentile",
    predicted_median: "Median",
    estimate75th: "75th Percentile",
    best_estimate: "Best Estimate",
    estimate5th: "5th Percentile",
    estimate10th: "10th Percentile",
    estimate90th: "90th Percentile",
    estimate95th: "95th Percentile",
  };
  return (
    names[slug] ||
    slug.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())
  );
}

export default useForecastTrends;
